import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    getOpenApiCancelWarehouseDeliveryIntakeUrl,
    getOpenApiGetWarehouseDeliveryIntakesUrl,
    OpenApiCancelWarehouseDeliveryIntakeMethod,
    type OpenApiCancelWarehouseDeliveryIntakePathParameters,
    type OpenApiCancelWarehouseDeliveryIntakeRequest,
    type OpenApiCancelWarehouseDeliveryIntakeResponseSuccess,
    type OpenApiCancelWarehouseDeliveryIntakeSearchParams,
    OpenApiGetWarehouseDeliveryIntakesMethod,
    type OpenApiGetWarehouseDeliveryIntakesPathParameters,
    type OpenApiGetWarehouseDeliveryIntakesResponseSuccess,
    type OpenApiGetWarehouseDeliveryIntakesSearchParams,
} from '#shared/api/schema';
import { InvalidateGridApiHelper } from '#shared/lib/requests';

export type GetWarehouseDeliveryIntakesParams = OpenApiGetWarehouseDeliveryIntakesSearchParams &
    OpenApiGetWarehouseDeliveryIntakesPathParameters;

export type CancelWarehouseDeliveryIntakeParams = OpenApiCancelWarehouseDeliveryIntakePathParameters &
    OpenApiCancelWarehouseDeliveryIntakeSearchParams & {
        payload: OpenApiCancelWarehouseDeliveryIntakeRequest;
    };

const WarehouseDeliveryIntakesListTag = 'WarehouseDeliveryIntakesListTag' as const;

type WarehouseDeliveryIntakesListTags = typeof WarehouseDeliveryIntakesListTag;

export const warehouseDeliveryIntakesTagInvalidator = new InvalidateGridApiHelper<
    WarehouseDeliveryIntakesListTags,
    GetWarehouseDeliveryIntakesParams
>(WarehouseDeliveryIntakesListTag, 'warehouseDeliveryIntakes');

const apiWarehouseIntakes = createApi({
    name: 'entities/warehouse/api/warehouseIntakes',
    tagTypes: [WarehouseDeliveryIntakesListTag],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getWarehouseDeliveryIntakesList: builder.query<
                OpenApiGetWarehouseDeliveryIntakesResponseSuccess,
                GetWarehouseDeliveryIntakesParams
            >({
                query: ({ warehouse_id, ...params }) => {
                    return {
                        url: getOpenApiGetWarehouseDeliveryIntakesUrl({ warehouse_id }),
                        method: OpenApiGetWarehouseDeliveryIntakesMethod,
                        params,
                    };
                },
                providesTags: (result, error, args) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        { type: WarehouseDeliveryIntakesListTag, id: 'grid' },
                        ...warehouseDeliveryIntakesTagInvalidator.provideHashTagForGridEndpoint(args),
                    ];
                },
                onQueryStarted() {
                    apiWarehouseIntakes.util.invalidateTags(
                        warehouseDeliveryIntakesTagInvalidator.invalidateHashTagForGridEndpoint()
                    );
                },
            }),
            cancelWarehouseDeliveryIntake: builder.mutation<
                OpenApiCancelWarehouseDeliveryIntakeResponseSuccess,
                CancelWarehouseDeliveryIntakeParams
            >({
                query: ({ warehouse_id, service_type, payload }) => {
                    return {
                        url: getOpenApiCancelWarehouseDeliveryIntakeUrl({ warehouse_id }),
                        method: OpenApiCancelWarehouseDeliveryIntakeMethod,
                        params: { service_type },
                        body: payload,
                    };
                },
                async onQueryStarted({ warehouse_id, service_type, payload }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    apiWarehouseIntakes.util.invalidateTags(
                        warehouseDeliveryIntakesTagInvalidator.getTagsToInvalidate()
                    );

                    const lastArgs = warehouseDeliveryIntakesTagInvalidator.lastArgs;

                    if (lastArgs && lastArgs.warehouse_id === warehouse_id && lastArgs.service_type === service_type) {
                        apiWarehouseIntakes.endpoints.getWarehouseDeliveryIntakesList.updateQueryData(
                            lastArgs,
                            (draft) => {
                                if (!draft) {
                                    return;
                                }

                                const { from, to } = payload.interval;

                                draft.warehouse_delivery_intakes = draft.warehouse_delivery_intakes.filter(
                                    (intake) => !(intake.interval?.from === from && intake.interval?.to === to)
                                );

                                draft.total_count = Math.max(0, draft.total_count - 1);
                            }
                        );
                    }
                },
            }),
        };
    },
});

export const endpointGetWarehouseDeliveryIntakesList = apiWarehouseIntakes.endpoints.getWarehouseDeliveryIntakesList;
export const useGetWarehouseDeliveryIntakesList = endpointGetWarehouseDeliveryIntakesList.useQuery;

export const invalidateWarehouseDeliveryIntakesListCache = () =>
    apiWarehouseIntakes.util.invalidateTags([WarehouseDeliveryIntakesListTag]);

export const cancelWarehouseDeliveryIntake = apiWarehouseIntakes.endpoints.cancelWarehouseDeliveryIntake.mutate;
